import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api, orderService } from '../../services/api';
import TpvSettings from './TpvSettings';
import TicketHistory from './TicketHistory';
import { useTpvSettings } from '../../hooks/useTpvSettings';
import { useAuth } from '../../contexts/AuthContext';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const DrawerContainer = styled.div`
  position: ${props => props.$staticMode ? 'relative' : 'fixed'};
  
  ${props => props.$staticMode ? `
    /* Modo estático - columna derecha */
    width: 100%;
    height: fit-content;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
  ` : `
    /* Modo overlay - comportamiento original */
    top: 0;
    right: ${props.$isOpen ? '0' : '-600px'};
    bottom: 0;
    width: 600px;
    background: #f5f5f5;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
    z-index: 1001;
    transition: right 0.3s ease;
    display: flex;
    flex-direction: column;
    border-left: 3px solid #2196F3;
    height: 100vh;
    overflow: hidden;

    @media (max-width: 768px) {
      width: 100%;
      right: ${props.$isOpen ? '0' : '-100%'};
    }
  `}
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.$staticMode ? '0.5rem 0.75rem' : '1rem'};
  border-bottom: 2px solid #ddd;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  flex-shrink: 0;
`;

const DrawerTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: ${props => props.$staticMode ? '1.1rem' : '1.3rem'};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SettingsButton = styled.button`
  background: #f8f9fa;
  border: 1px solid #ddd;
  color: #666;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: #e9ecef;
    color: #333;
    border-color: #007bff;
  }
`;

const HistoryButton = styled.button`
  background: #f8f9fa;
  border: 1px solid #ddd;
  color: #666;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: #e9ecef;
    color: #333;
    border-color: #28a745;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: #f0f0f0;
    color: #333;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;
`;

const TicketSection = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const TicketHeader = styled.div`
  padding: ${props => props.$staticMode ? '0.5rem 0.75rem' : '0.75rem 1rem'};
  background: #f8f9fa;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TicketItems = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.$staticMode ? '0.25rem' : '0.5rem'};
  min-height: 0;
  max-height: ${props => props.$staticMode ? '250px' : 'calc(100vh - 400px)'};
  
  @media (max-width: 768px) {
    max-height: ${props => props.$staticMode ? '250px' : 'calc(100vh - 450px)'};
  }
`;

const TicketItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 120px auto;
  gap: ${props => props.$staticMode ? '0.5rem' : '0.75rem'};
  padding: ${props => props.$staticMode ? '0.5rem' : '0.75rem'};
  border-bottom: 1px solid #eee;
  align-items: center;
  transition: background 0.2s;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemName = styled.div`
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const ItemQuantity = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  background: white;
  cursor: pointer;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #666;
  transition: all 0.2s;
  
  &:hover {
    background: #f0f0f0;
    border-color: #999;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const QuantityDisplay = styled.input`
  width: 50px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.25rem;
  font-weight: 600;
  
  &:focus {
    outline: none;
    border-color: #2196F3;
  }
`;

const ItemPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  input {
    width: 80px;
    padding: 0.25rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-weight: 600;
    text-align: right;
    font-size: 0.9rem;
    
    &:focus {
      outline: none;
      border-color: #2196F3;
    }
  }
  
  .price-display {
    font-weight: 600;
    color: #333;
    font-size: 0.95rem;
    cursor: pointer;
    
    &:hover {
      color: #2196F3;
    }
  }
`;

const DiscountControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
`;

const DiscountButton = styled.button`
  width: 24px;
  height: 24px;
  border: 1px solid #ddd;
  background: ${props => props.$active ? '#007bff' : 'white'};
  color: ${props => props.$active ? 'white' : '#666'};
  cursor: pointer;
  border-radius: 3px;
  font-size: 0.75rem;
  font-weight: 600;
  
  &:hover {
    background: ${props => props.$active ? '#0056b3' : '#f0f0f0'};
    border-color: #007bff;
  }
`;

const RemoveButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.2s;
  
  &:hover {
    background: #c82333;
    transform: scale(1.05);
  }
`;

const TicketFooter = styled.div`
  padding: 1rem;
  background: linear-gradient(to bottom, #f8f9fa, #fff);
  border-top: 2px solid #ddd;
  flex-shrink: 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
  font-size: ${props => props.$large ? '1.5rem' : '0.95rem'};
  font-weight: ${props => props.$large ? '700' : '400'};
  color: ${props => props.$large ? '#28a745' : '#666'};
  
  ${props => props.$large && `
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 2px solid #dee2e6;
  `}
`;

const GlobalDiscountSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-top: 1px solid #eee;
  margin-top: 0.5rem;
  
  .discount-label {
    font-size: 0.9rem;
    color: #666;
  }
  
  .discount-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .discount-input {
    width: 60px;
    padding: 0.25rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    text-align: center;
    font-size: 0.9rem;
    
    &:focus {
      outline: none;
      border-color: #2196F3;
    }
  }
  
  .discount-buttons {
    display: flex;
    gap: 0.25rem;
  }
`;

const ActionSection = styled.div`
  background: white;
  padding: 1rem;
  border-top: 2px solid #ddd;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  z-index: 10;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
`;

const PaymentMethods = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
`;

const PaymentButton = styled.button`
  width: 100%;
  padding: 0.75rem 0.5rem;
  border: 2px solid ${props => props.$selected ? '#007bff' : '#ddd'};
  background: ${props => props.$selected ? '#007bff' : 'white'};
  color: ${props => props.$selected ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  font-size: 0.875rem;

  &:hover {
    border-color: #007bff;
    ${props => !props.$selected && 'background: #f8f9fa;'}
  }

  span {
    font-size: 1.5rem;
  }
`;

const CashInputSection = styled.div`
  display: ${props => props.$show ? 'flex' : 'none'};
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: #f0f8ff;
  border-radius: 8px;
  margin-top: 0.5rem;
`;

const CashInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  label {
    font-weight: 500;
    color: #333;
    min-width: 100px;
  }
  
  input {
    flex: 1;
    padding: 0.75rem;
    font-size: 1.25rem;
    font-weight: 600;
    border: 2px solid #ddd;
    border-radius: 4px;
    text-align: right;
    
    &:focus {
      outline: none;
      border-color: #2196F3;
      background: #fff;
    }
  }
`;

const ChangeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  background: ${props => props.$positive ? '#d4edda' : '#f8d7da'};
  border-radius: 4px;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.$positive ? '#155724' : '#721c24'};
`;

const QuickAmountButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const QuickAmountButton = styled.button`
  padding: 0.75rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e9ecef;
    transform: scale(1.02);
  }
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 1.25rem;
  background: ${props => {
    if (props.$variant === 'danger') return '#dc3545';
    if (props.$variant === 'secondary') return '#6c757d';
    return '#28a745';
  }};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => {
      if (props.$variant === 'danger') return '#c82333';
      if (props.$variant === 'secondary') return '#5a6268';
      return '#218838';
    }};
    transform: scale(1.02);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: scale(1);
  }
`;

const ActionButtonsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(23, 162, 184, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(23, 162, 184, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(23, 162, 184, 0);
  }
`;

const AddToTicketButton = styled.button`
  background: ${props => props.$hasNew ? '#28a745' : '#6c757d'};
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: ${props => props.$hasNew ? pulse : 'none'} 2s infinite;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$hasNew ? '0 2px 6px rgba(40, 167, 69, 0.3)' : '0 1px 3px rgba(0,0,0,0.1)'};
  
  &:hover {
    background: ${props => props.$hasNew ? '#218838' : '#5a6268'};
    transform: translateY(-1px);
    box-shadow: ${props => props.$hasNew ? '0 4px 12px rgba(40, 167, 69, 0.4)' : '0 2px 6px rgba(0,0,0,0.15)'};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    animation: none;
    background: #6c757d;
  }
`;

const EmptyTicket = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  padding: 2rem;
  text-align: center;
  
  p {
    margin: 0.5rem 0;
  }
`;

const FloatingTpvButton = styled.button`
  position: fixed;
  right: ${props => props.$isOpen ? '-100px' : '20px'};
  bottom: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #28a745;
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  cursor: pointer;
  font-size: 1.5rem;
  z-index: 1000;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(40, 167, 69, 0.4);
  }
  
  ${props => props.$hasItems && `
    &::after {
      content: '${props.$itemCount}';
      position: absolute;
      top: -5px;
      right: -5px;
      background: #dc3545;
      color: white;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.875rem;
      font-weight: bold;
    }
  `}
`;

const NumpadModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  z-index: 1100;
  padding: 1.5rem;
  display: ${props => props.$show ? 'block' : 'none'};
  min-width: 320px;
`;

const NumpadDisplay = styled.div`
  background: #2c3e50;
  color: #00ff00;
  font-size: 2rem;
  padding: 1rem;
  text-align: right;
  font-family: 'Courier New', monospace;
  margin-bottom: 1rem;
  border-radius: 4px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const NumpadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const NumpadButton = styled.button`
  padding: 1.25rem;
  font-size: 1.5rem;
  font-weight: 600;
  border: 1px solid #ddd;
  background: ${props => props.$special ? '#f8f9fa' : 'white'};
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.1s;
  
  &:hover {
    background: #e9ecef;
    transform: scale(0.98);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  ${props => props.$wide && `
    grid-column: span 2;
  `}
  
  ${props => props.$accent && `
    background: #007bff;
    color: white;
    border-color: #007bff;
    
    &:hover {
      background: #0056b3;
    }
  `}
  
  ${props => props.$danger && `
    background: #dc3545;
    color: white;
    border-color: #dc3545;
    
    &:hover {
      background: #c82333;
    }
  `}
  
  ${props => props.$success && `
    background: #28a745;
    color: white;
    border-color: #28a745;
    
    &:hover {
      background: #218838;
    }
  `}
`;

const PostActionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1200;
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
`;

const PostActionModal = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  padding: 2rem;
  min-width: 360px;
  max-width: 450px;
  text-align: center;
`;

const PostActionTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.3rem;
  color: #333;
`;

const PostActionAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.$paid ? '#28a745' : '#007bff'};
  margin-bottom: 1.5rem;
`;

const PostActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PostActionButton = styled.button`
  width: 100%;
  padding: 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  background: ${props => {
    if (props.$variant === 'primary') return '#007bff';
    if (props.$variant === 'success') return '#28a745';
    return '#6c757d';
  }};
  color: white;

  &:hover {
    transform: scale(1.02);
    background: ${props => {
      if (props.$variant === 'primary') return '#0056b3';
      if (props.$variant === 'success') return '#218838';
      return '#5a6268';
    }};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: scale(1);
  }
`;

const TpvDrawer = ({ isOpen, onClose, calculation, formData, onPaymentSuccess, onTicketItemsChange, staticMode = false, externalAddToTicketSignal = 0 }) => {
  const { isAdmin } = useAuth();
  const [ticketItems, setTicketItems] = useState([]);
  const [lastAddedCalculation, setLastAddedCalculation] = useState(null);
  const [lastToastMessage, setLastToastMessage] = useState('');
  const [toastTimeout, setToastTimeout] = useState(null);
  const [showCashInput, setShowCashInput] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [showNumpad, setShowNumpad] = useState(false);
  const [numpadValue, setNumpadValue] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [globalDiscountType, setGlobalDiscountType] = useState('%');
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [targetDeliveryDate, setTargetDeliveryDate] = useState('');
  const [showPostActionModal, setShowPostActionModal] = useState(false);
  const [postActionOrderId, setPostActionOrderId] = useState(null);
  const [postActionType, setPostActionType] = useState(null); // 'save' or 'charge'
  const [postActionTotal, setPostActionTotal] = useState(0);
  const [isPrintingPdf, setIsPrintingPdf] = useState(false);

  const queryClient = useQueryClient();
  const tpvSettings = useTpvSettings();

  // Get active TPV session
  const { data: sessionData, isLoading: sessionLoading, error: sessionError, refetch: refetchSession } = useQuery({
    queryKey: ['tpv-session', 'active'],
    queryFn: () => api.get('/tpv/session/active'),
    enabled: isOpen,
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  const activeSession = sessionData?.data?.data?.session;

  // Smart toast system to prevent duplicates
  const showToast = (type, message, options = {}) => {
    const key = `${type}-${message}`;
    if (lastToastMessage === key && Date.now() - (toastTimeout || 0) < 2000) {
      return; // Skip duplicate within 2 seconds
    }
    
    setLastToastMessage(key);
    setToastTimeout(Date.now());
    
    switch (type) {
      case 'success':
        toast.success(message, options);
        break;
      case 'info':
        toast.info(message, options);
        break;
      case 'warning':
        toast.warning(message, options);
        break;
      case 'error':
        toast.error(message, options);
        break;
      default:
        toast(message, options);
    }
  };

  // Add current calculation to ticket when button is clicked
  const handleAddToTicket = () => {
    if (calculation && calculation.fingerprint !== lastAddedCalculation?.fingerprint) {
      // Use the same displayName that appears in the calculation section
      const buildItemName = () => {
        return calculation.displayName || 'Artículo';
      };
      
      // Debug the calculation values
      console.log('Calculation values:', {
        subtotal: calculation.subtotal,
        total: calculation.total,
        tax: calculation.tax,
        items: calculation.items
      });
      
      // Calculate the total correctly
      // IMPORTANT: The price with margin (70%) ALREADY INCLUDES VAT
      // So calculation.subtotal is actually the price WITH VAT included
      let priceWithVat;
      if (calculation.subtotal && calculation.subtotal > 0) {
        // subtotal from backend = price with margin = price WITH VAT
        priceWithVat = calculation.subtotal / (calculation.units || 1);
      } else if (calculation.total && calculation.total > 0) {
        // Fallback: use total directly (already with VAT)
        priceWithVat = calculation.total / (calculation.units || 1);
      } else {
        // Last fallback: sum items and apply margin estimate
        const rawTotal = calculation.items?.reduce((sum, item) => sum + (item.totalPrice || 0), 0) || 0;
        priceWithVat = rawTotal * 1.7; // Apply 70% margin (which includes VAT)
      }

      const unitPrice = priceWithVat;
      const itemQuantity = calculation.units || 1;

      const newItem = {
        id: Date.now(),
        name: buildItemName(),
        quantity: itemQuantity,
        unitPrice: unitPrice,  // Price per unit WITH VAT included
        originalPrice: unitPrice,
        total: unitPrice * itemQuantity,  // Total = unit price × quantity
        itemDiscount: 0,
        itemDiscountType: '%',
        calculation: calculation // Keep full calculation for order creation
      };

      console.log('TPV: Adding item with price (VAT included):', priceWithVat, 'from calculation:', calculation);
      setTicketItems([...ticketItems, newItem]);
      setLastAddedCalculation(calculation);
      showToast('success', 'Artículo añadido al ticket');
    } else if (calculation && calculation.fingerprint === lastAddedCalculation?.fingerprint) {
      showToast('info', 'Artículo ya añadido al ticket');
    }
  };

  // Update quantity
  const updateQuantity = (itemId, delta) => {
    setTicketItems(items => 
      items.map(item => {
        if (item.id === itemId) {
          const newQty = Math.max(1, item.quantity + delta);
          return {
            ...item,
            quantity: newQty,
            total: calculateItemTotal(item.unitPrice, newQty, item.itemDiscount, item.itemDiscountType)
          };
        }
        return item;
      })
    );
  };

  // Calculate item total with discount
  const calculateItemTotal = (unitPrice, quantity, discount, discountType) => {
    const baseTotal = unitPrice * quantity;
    if (discount === 0) return baseTotal;
    
    if (discountType === '%') {
      return baseTotal * (1 - discount / 100);
    } else {
      return Math.max(0, baseTotal - discount);
    }
  };

  // Handle quantity edit
  const handleQuantityEdit = (itemId, value) => {
    const qty = parseInt(value) || 1;
    setTicketItems(items => 
      items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            quantity: qty,
            total: calculateItemTotal(item.unitPrice, qty, item.itemDiscount, item.itemDiscountType)
          };
        }
        return item;
      })
    );
  };

  // Handle unit price edit
  const handleUnitPriceEdit = (itemId, value) => {
    const price = parseFloat(value) || 0;
    setTicketItems(items => 
      items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            unitPrice: price,
            total: calculateItemTotal(price, item.quantity, item.itemDiscount, item.itemDiscountType)
          };
        }
        return item;
      })
    );
  };

  // Handle item discount
  const handleItemDiscount = (itemId, discount, discountType) => {
    setTicketItems(items => 
      items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            itemDiscount: discount,
            itemDiscountType: discountType,
            total: calculateItemTotal(item.unitPrice, item.quantity, discount, discountType)
          };
        }
        return item;
      })
    );
  };

  // Apply quick discount/increment with confirmation if needed
  const applyQuickDiscount = (itemId, percentage) => {
    if (tpvSettings.shouldRequireDiscountConfirmation(percentage)) {
      if (!window.confirm(`¿Estás seguro de aplicar un ${percentage > 0 ? 'incremento' : 'descuento'} del ${Math.abs(percentage)}%?`)) {
        return;
      }
    }
    handleItemDiscount(itemId, percentage, '%');
  };

  // Remove item from ticket
  const removeItem = (itemId) => {
    setTicketItems(items => items.filter(item => item.id !== itemId));
    showToast('info', 'Artículo eliminado');
  };

  // Helper function to round to 5 cents (for final price WITH VAT)
  const roundToFiveCents = (number) => {
    return Math.ceil(number * 20) / 20;
  };

  // Cuadra los componentes de cada artículo para que, al reconstruir el precio en la vista de
  // detalle / Odoo / PDF (suma de componentes × 1.21, redondeado hacia arriba), salga EXACTAMENTE
  // el precio canónico que ya redondeó la calculadora. Sin esto, los componentes se guardan a 2
  // decimales y su suma "deriva" un céntimo, haciendo que el detalle re-redondee de más
  // (p.ej. 114,35 canónico se mostraba como 114,40). Solo ajusta el céntimo de reparto.
  const snapOrderItemsToCanonical = (orderItems, sourceTicketItems) => {
    sourceTicketItems.forEach(ti => {
      const group = orderItems.filter(line =>
        line.metadata?.fingerprint === ti.calculation?.fingerprint &&
        line.metadata?.calculationIndex === ti.id
      );
      if (group.length === 0) return;
      const units = ti.calculation?.units || 1;
      const disc = ti.itemDiscount || 0;
      const canonicalUnitWithVat = (parseFloat(ti.calculation?.subtotal) / units) * (1 - disc / 100);
      if (!canonicalUnitWithVat || !isFinite(canonicalUnitWithVat)) return;
      // Objetivo (neto por unidad): floor para que roundToFiveCents(neto × 1.21) = precio canónico
      const targetNet = Math.floor((canonicalUnitWithVat / 1.21) * 100) / 100;
      group.forEach(c => {
        c.totalPrice = Math.round((c.totalPrice || 0) * 100) / 100;
        c.unitPrice = c.totalPrice;
      });
      const sumNet = Math.round(group.reduce((s, c) => s + (c.totalPrice || 0), 0) * 100) / 100;
      const diff = Math.round((targetNet - sumNet) * 100) / 100;
      if (diff !== 0) {
        const largest = group.reduce((a, b) => ((b.totalPrice || 0) > (a.totalPrice || 0) ? b : a), group[0]);
        largest.totalPrice = Math.round((largest.totalPrice + diff) * 100) / 100;
        largest.unitPrice = largest.totalPrice;
      }
    });
    return orderItems;
  };

  // Calculate total WITH VAT - items already have VAT included (margin 70% = price with VAT)
  const calculateTotalWithVat = () => {
    const totalWithVat = ticketItems.reduce((sum, item) => {
      // item.total already includes VAT (price with 70% margin)
      return sum + (item.total || 0);
    }, 0);
    return totalWithVat;
  };

  const calculateGlobalDiscountAmount = (totalWithVat) => {
    if (globalDiscount === 0) return 0;

    // El descuento se aplica sobre el precio con IVA (precio final)
    if (globalDiscountType === '%') {
      return totalWithVat * (globalDiscount / 100);
    } else {
      return Math.min(globalDiscount, totalWithVat);
    }
  };

  // Calculate total WITH VAT (rounded to 5 cents)
  const calculateTotal = () => {
    const totalWithVat = calculateTotalWithVat();
    const discountAmount = calculateGlobalDiscountAmount(totalWithVat);
    const afterDiscount = totalWithVat - discountAmount;
    // IMPORTANT: Round the final price WITH VAT to 5 cents
    return roundToFiveCents(afterDiscount);
  };

  // Calculate base (subtotal without VAT) - extract from total with VAT
  const calculateSubtotal = () => {
    const total = calculateTotal();
    // Base imponible = Total con IVA / 1.21
    return total / 1.21;
  };

  // Calculate tax (VAT) - difference between total and base
  const calculateTax = () => {
    const total = calculateTotal();
    const base = calculateSubtotal();
    // IVA = Total con IVA - Base imponible
    return total - base;
  };

  // Handle payment method selection
  const handlePaymentMethodSelect = (method) => {
    setPaymentMethod(method);
    if (method === 'cash') {
      setShowCashInput(true);
      const total = calculateTotal();
      // Suggest common amounts
      const roundedUp = Math.ceil(total / 5) * 5;
      setCashReceived(roundedUp.toFixed(2));
    } else if (method === 'mixed') {
      // For mixed payment, we might need partial cash
      setShowCashInput(true);
      const total = calculateTotal();
      // Default to half the total for mixed payments
      setCashReceived((total / 2).toFixed(2));
    } else {
      // For card and transfer, no cash input needed
      setShowCashInput(false);
      setCashReceived('');
    }
  };

  // Quick amount buttons
  const handleQuickAmount = (amount) => {
    setCashReceived(amount.toFixed(2));
  };

  // Calculate change
  const calculateChange = () => {
    const received = parseFloat(cashReceived) || 0;
    const total = calculateTotal();
    return received - total;
  };

  // Process payment
  const handleProcessPayment = async () => {
    // Prevent multiple simultaneous processing
    if (isProcessingPayment) {
      toast.warning('Ya se está procesando un pago. Por favor, espera.');
      return;
    }

    const total = calculateTotal();
    if (total === 0) {
      toast.warning('No hay artículos en el ticket');
      return;
    }

    if (!activeSession) {
      toast.error('No hay sesión TPV activa. Abre una sesión primero.');
      return;
    }

    // Check if payment method is selected
    if (!paymentMethod) {
      toast.error('Por favor, selecciona un método de pago antes de cobrar');
      return;
    }

    // Validate payment based on method
    if (paymentMethod === 'cash') {
      const change = calculateChange();
      if (change < 0) {
        toast.error(`El importe recibido es insuficiente. Faltan ${formatCurrency(Math.abs(change))}`);
        return;
      }
    } else if (paymentMethod === 'mixed') {
      // For mixed payments, cash received should be partial
      const cashPart = parseFloat(cashReceived) || 0;
      if (cashPart <= 0) {
        toast.warning('Para pago mixto, especifica la parte en efectivo');
        return;
      }
      if (cashPart >= total) {
        toast.info('El importe en efectivo cubre el total. Considera usar pago en efectivo completo.');
      }
    }
    // For card and transfer, no validation needed

    // Set processing flag to prevent duplicate submissions
    setIsProcessingPayment(true);

    try {
      let selectedClient = null;

      // If a client was selected in formData (from CalculatorPro), use it
      if (formData?.clientId) {
        try {
          const clientResponse = await api.get(`/clients/${formData.clientId}`);
          selectedClient = clientResponse.data.client || clientResponse.data;
          console.log('Using selected client from formData:', selectedClient.name);
        } catch (error) {
          console.error('Error fetching selected client:', error);
          toast.warning('No se pudo cargar el cliente seleccionado, usando cliente genérico');
        }
      }

      // If no client selected or error fetching, use a default/generic TPV client
      if (!selectedClient) {
        const clientsResponse = await api.get('/clients?type=particular');
        selectedClient = clientsResponse.data.clients?.find(c =>
          c.name?.toLowerCase().includes('genérico') ||
          c.name?.toLowerCase().includes('generico') ||
          c.name?.toLowerCase().includes('mostrador') ||
          c.name?.toLowerCase().includes('tpv')
        );

        // If no default client exists, create one
        if (!selectedClient) {
          try {
            const createClientResponse = await api.post('/clients', {
              name: 'Cliente Mostrador',
              email: 'tpv@tienda.local',
              phone: '000000000',
              address: 'Venta en mostrador',
              isCompany: false,
              notes: 'Cliente genérico para ventas TPV'
            });
            selectedClient = createClientResponse.data.client || createClientResponse.data;
          } catch (clientError) {
            console.error('Error creating default client:', clientError);
            // If can't create client, try to use the first available client
            if (clientsResponse.data.clients?.length > 0) {
              selectedClient = clientsResponse.data.clients[0];
              console.log('Using first available client:', selectedClient);
            } else {
              throw new Error('No se puede proceder sin un cliente. Por favor, cree un cliente primero.');
            }
          }
        }
      }

      const defaultClient = selectedClient; // Keep the variable name for compatibility

      // Build detailed description from ticket items
      const buildDetailedDescription = () => {
        if (ticketItems.length === 0) return 'Venta directa TPV';
        
        // If there's a calculation with full details, use it
        const itemWithCalc = ticketItems.find(item => item.calculation);
        if (itemWithCalc && itemWithCalc.calculation) {
          const calc = itemWithCalc.calculation;
          let description = '';
          
          // Add fingerprint if available
          if (calc.fingerprint) {
            description += `Huella: ${calc.fingerprint}\n`;
          }
          
          // Add measurement info
          if (calc.measurementType && calc.width && calc.height) {
            description += `${calc.measurementType} - ${calc.width}x${calc.height}`;
          }
          
          // Add items details
          if (calc.items && calc.items.length > 0) {
            calc.items.forEach(item => {
              if (item.productName) {
                description += ` -${item.productName}`;
              }
            });
          }
          
          // Add fingerprint at the end
          if (calc.fingerprint) {
            description += ` *${calc.fingerprint}`;
          }
          
          return description || calc.displayName || 'Venta TPV';
        }
        
        // Otherwise, list the items
        return ticketItems.map(item => item.name).join(', ');
      };

      // Prepare items for the order (calculator expects specific format)
      const orderItems = ticketItems.map(item => {
        // For items from calculation, we need to preserve the structure but use correct prices
        if (item.calculation && item.calculation.items && item.calculation.items.length > 0) {
          // The calculation.items have base prices without margin
          // The calculation.subtotal has the total with margin
          // We need to apply the same margin to individual items
          
          const rawItemsTotal = item.calculation.items.reduce((sum, calcItem) => 
            sum + (calcItem.totalPrice || 0), 0);
          
          // Calculate the margin that was applied
          const marginMultiplier = rawItemsTotal > 0 ? 
            (item.calculation.subtotal / item.calculation.units) / rawItemsTotal : 1.5;
          
          console.log('Applying margin to items:', {
            rawItemsTotal,
            subtotalPerUnit: item.calculation.subtotal / item.calculation.units,
            marginMultiplier
          });
          
          const ticketQty = item.quantity || 1;
          return item.calculation.items.map(calcItem => {
            // Apply the same margin that was used in the calculation
            let itemPriceWithMargin = (calcItem.totalPrice || 0) * marginMultiplier;

            // Apply the item discount/increase to this material's price
            if (item.itemDiscount && item.itemDiscount !== 0) {
              itemPriceWithMargin = itemPriceWithMargin * (1 - (item.itemDiscount / 100));
            }

            // Convert to base (without VAT) - TPV prices include VAT, we store base like CalculatorPro
            const itemPriceBase = itemPriceWithMargin / 1.21;

            // Send per-unit price — backend multiplies by quantity
            return {
              ...calcItem,
              type: calcItem.type || 'accesorio1',
              quantity: ticketQty,
              unitPrice: itemPriceBase,
              totalPrice: itemPriceBase,  // Per unit — backend will multiply by quantity
              discountPercentage: item.itemDiscount || 0,
              isPriceManual: true,
              metadata: {
                ...calcItem.metadata,
                fingerprint: item.calculation.fingerprint,
                calculationIndex: item.id || Date.now(),
                itemDiscount: item.itemDiscount,
                itemDiscountType: item.itemDiscountType,
                originalMeasurementType: item.calculation.measurementType,
                originalWidth: item.calculation.width,
                originalHeight: item.calculation.height
              }
            };
          });
        }
        // Otherwise create a simple item structure
        // Convert to base (without VAT) - TPV prices include VAT, we store base like CalculatorPro
        const itemUnitPriceBase = (item.unitPrice || 0) / 1.21;
        return {
          type: 'accesorio1',
          productId: item.productId || null,
          productName: item.name,
          quantity: item.quantity,
          unitPrice: itemUnitPriceBase,
          totalPrice: itemUnitPriceBase,  // Per unit — backend will multiply by quantity
          discountPercentage: item.itemDiscount || 0,
          metadata: {
            discount: item.itemDiscount,
            discountType: item.itemDiscountType,
            fingerprint: item.calculation?.fingerprint || `single-${item.id}`,
            calculationIndex: item.id || Date.now()
          }
        };
      }).flat();

      // Cuadrar componentes para que el detalle/Odoo/PDF reconstruyan el precio canónico (sin derivar céntimos)
      snapOrderItemsToCanonical(orderItems, ticketItems);

      // Calculate amounts - item.total includes VAT, but orderItems now store BASE (without VAT)
      // The discount is applied on the visible price (with VAT) as the customer sees it
      const totalWithVat = ticketItems.reduce((sum, item) => sum + (item.total || 0), 0);
      const discountAmountWithVat = globalDiscount === 0 ? 0 :
        (globalDiscountType === '%' ?
          totalWithVat * (globalDiscount / 100) :
          Math.min(globalDiscount, totalWithVat));
      const finalTotalWithVat = roundToFiveCents(totalWithVat - discountAmountWithVat);

      // Extract base (without VAT) from final total for display consistency
      const baseSubtotal = finalTotalWithVat / 1.21;  // Base imponible
      const baseDiscountAmount = discountAmountWithVat / 1.21;  // Descuento sobre base
      const baseAfterDiscount = baseSubtotal;  // After discount (already applied in finalTotalWithVat)
      const taxAmount = finalTotalWithVat - baseSubtotal;  // IVA = Total - Base
      const finalTotal = finalTotalWithVat;  // Total con IVA

      // Log to debug
      console.log('Creating order with client:', defaultClient);
      console.log('Order items prepared:', orderItems);
      console.log('Active session:', activeSession);
      console.log('Notes from formData:', formData?.notes);
      console.log('Notes from calculation:', calculation?.notes);
      console.log('Price calculations:', {
        baseSubtotal,
        baseDiscountAmount,
        baseAfterDiscount,
        taxAmount,
        finalTotal
      });
      
      // Create order data
      const orderNotes = formData?.notes || calculation?.notes || `Venta TPV #${activeSession.sessionNumber}`;
      
      const orderData = {
        calculation: {
          client: {
            id: defaultClient.id,
            name: defaultClient.name
          },
          total: finalTotal,  // Total with VAT
          pricePerUnit: baseAfterDiscount / ticketItems.reduce((sum, item) => sum + item.quantity, 0),
          units: ticketItems.reduce((sum, item) => sum + item.quantity, 0),
          subtotal: baseSubtotal,  // Subtotal without VAT
          tax: taxAmount,
          discount: globalDiscountType === '%' ? globalDiscount : 0,
          discountAmount: baseDiscountAmount,
          displayName: buildDetailedDescription(),
          fingerprint: ticketItems[0]?.calculation?.fingerprint || `tpv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          items: orderItems.length > 0 ? orderItems : [{
            type: 'accesorio1',  // Use valid enum value
            productId: null,
            productName: 'Venta directa TPV',
            quantity: 1,
            unitPrice: baseAfterDiscount,  // Already without VAT
            totalPrice: baseAfterDiscount,  // Already without VAT
            metadata: {}
          }],
          notes: orderNotes,
          measurementType: ticketItems[0]?.calculation?.measurementType || 'INT',
          width: ticketItems[0]?.calculation?.width || 0,
          height: ticketItems[0]?.calculation?.height || 0
        },
        notes: orderNotes,  // Also pass notes at the top level for the backend
        clientReference: `TPV-${activeSession.sessionNumber}-${Date.now()}`,
        supplierReference: '',
        metadata: {
          isTPVSale: true,
          cashReceived: parseFloat(cashReceived) || 0,
          change: calculateChange(),
          sessionId: activeSession.id,
          paymentMethod,
          globalDiscount,
          globalDiscountType,
          timestamp: new Date().toISOString(),
          targetDeliveryDate: targetDeliveryDate || null
        },
        targetDeliveryDate: targetDeliveryDate || null
      };

      // Log the final order data
      console.log('Final order data to send:', JSON.stringify(orderData, null, 2));
      console.log('Order items detail:', orderItems.map(item => ({
        productName: item.productName || item.name,
        unitPrice: item.unitPrice,
        quantity: item.quantity,
        totalPrice: item.totalPrice,
        squareMeters: item.squareMeters
      })));
      console.log('Base subtotal being sent:', baseSubtotal);
      
      // Create the order
      const savedOrder = await api.post('/calculator/save-order', orderData);
      console.log('Order created response:', savedOrder.data);
      const orderId = savedOrder.data.order?.id;

      // Check if order was created successfully
      if (!orderId) {
        throw new Error('No se pudo crear el pedido');
      }

      // Process the TPV transaction - MUST be done BEFORE invalidating cache
      // so the order appears with tpvTransaction in the list
      const transactionResult = await api.post('/tpv/transaction', {
        orderId: orderId,
        paymentMethod: paymentMethod,
        sessionId: activeSession.id
      });

      const transaction = transactionResult.data.data?.transaction || transactionResult.data.transaction;

      // Invalidate orders cache AFTER creating the TPV transaction
      // This ensures the order appears as TPV (not PRO) in the list
      queryClient.invalidateQueries(['orders']);
      
      // Success messages based on payment method
      toast.success(`✅ Venta completada: ${formatCurrency(total)}`);
      toast.success(`🧾 Ticket #${transaction?.transactionNumber || 'TPV'}`);
      
      // Show payment method specific messages
      if (paymentMethod === 'cash') {
        const change = calculateChange();
        if (change > 0) {
          toast.info(`💵 Cambio a devolver: ${formatCurrency(change)}`, {
            autoClose: 10000,
            style: { background: '#d4edda', color: '#155724' }
          });
        } else {
          toast.success('💵 Pago exacto recibido');
        }
      } else if (paymentMethod === 'card') {
        toast.success('💳 Pago con tarjeta procesado');
      }/* else if (paymentMethod === 'transfer') {
        toast.success('🏦 Transferencia registrada');
      } else if (paymentMethod === 'mixed') {
        const cashPart = parseFloat(cashReceived) || 0;
        const cardPart = total - cashPart;
        toast.success(`🔄 Pago mixto: ${formatCurrency(cashPart)} efectivo + ${formatCurrency(cardPart)} tarjeta`);
      }*/

      // Sync to Odoo
      try {
        const syncResult = await api.post(`/orders/${orderId}/sync-odoo`);
        if (syncResult.data.success) {
          toast.success('📤 Sincronizado con Odoo');
        }
      } catch (syncError) {
        console.error('Odoo sync error:', syncError);
        toast.warning('⚠️ No se pudo sincronizar con Odoo');
      }

      // Clear ticket
      setTicketItems([]);
      setCashReceived('');
      setShowCashInput(false);
      setGlobalDiscount(0);
      setTargetDeliveryDate('');

      if (onPaymentSuccess) {
        onPaymentSuccess({
          total,
          items: ticketItems,
          paymentMethod,
          cashReceived: parseFloat(cashReceived) || 0,
          change: calculateChange(),
          transaction: transaction,
          order: savedOrder.data.order
        });
      }

      // Show post-action modal in Venta Tienda mode
      if (staticMode && orderId) {
        openPostActionModal(orderId, 'charge', total);
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        config: error.config?.data
      });
      
      // Show more specific error message
      if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.errors?.[0]?.msg || 
                        error.response?.data?.message || 
                        error.response?.data?.error ||
                        'Datos inválidos';
        toast.error(`Error de validación: ${errorMsg}`);
      } else {
        toast.error(`Error al procesar el pago: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      // Always reset the processing flag
      setIsProcessingPayment(false);
    }
  };

  // Save order only (no payment processing, no Odoo sync)
  const handleSaveOnly = async () => {
    if (isSaving) {
      toast.warning('Ya se está guardando. Por favor, espera.');
      return;
    }

    const total = calculateTotal();
    if (total === 0) {
      toast.warning('No hay artículos en el ticket');
      return;
    }

    if (!activeSession) {
      toast.error('No hay sesión TPV activa. Abre una sesión primero.');
      return;
    }

    setIsSaving(true);

    try {
      // Get client (same logic as handleProcessPayment)
      let selectedClient = null;
      if (formData?.clientId) {
        try {
          const clientResponse = await api.get(`/clients/${formData.clientId}`);
          selectedClient = clientResponse.data.client || clientResponse.data;
        } catch (error) {
          console.error('Error fetching selected client:', error);
        }
      }

      if (!selectedClient) {
        const clientsResponse = await api.get('/clients?type=particular');
        selectedClient = clientsResponse.data.clients?.find(c =>
          c.name?.toLowerCase().includes('genérico') ||
          c.name?.toLowerCase().includes('mostrador') ||
          c.name?.toLowerCase().includes('tpv')
        );

        if (!selectedClient) {
          const createClientResponse = await api.post('/clients', {
            name: 'Cliente Mostrador',
            email: 'tpv@tienda.local',
            phone: '000000000',
            address: 'Venta en mostrador',
            isCompany: false,
            notes: 'Cliente genérico para ventas TPV'
          });
          selectedClient = createClientResponse.data.client || createClientResponse.data;
        }
      }

      // Build order items (same logic as handleProcessPayment)
      // Convert prices to BASE (without VAT) for consistent storage
      const orderItems = ticketItems.map(item => {
        if (item.calculation && item.calculation.items && item.calculation.items.length > 0) {
          const rawItemsTotal = item.calculation.items.reduce((sum, calcItem) =>
            sum + (calcItem.totalPrice || 0), 0);
          const marginMultiplier = rawItemsTotal > 0 ?
            (item.calculation.subtotal / item.calculation.units) / rawItemsTotal : 1.5;

          const ticketQty = item.quantity || 1;
          return item.calculation.items.map(calcItem => {
            let itemPriceWithMargin = (calcItem.totalPrice || 0) * marginMultiplier;
            if (item.itemDiscount && item.itemDiscount !== 0) {
              itemPriceWithMargin = itemPriceWithMargin * (1 - (item.itemDiscount / 100));
            }
            // Convert to base (without VAT) - TPV prices include VAT, we store base like CalculatorPro
            const itemPriceBase = itemPriceWithMargin / 1.21;
            // Send per-unit price — backend multiplies by quantity
            return {
              ...calcItem,
              type: calcItem.type || 'accesorio1',
              quantity: ticketQty,
              unitPrice: itemPriceBase,
              totalPrice: itemPriceBase,  // Per unit — backend will multiply by quantity
              discountPercentage: item.itemDiscount || 0,
              isPriceManual: true,
              metadata: {
                ...calcItem.metadata,
                fingerprint: item.calculation.fingerprint,
                calculationIndex: item.id || Date.now(),
                itemDiscount: item.itemDiscount,
                itemDiscountType: item.itemDiscountType,
                originalMeasurementType: item.calculation.measurementType,
                originalWidth: item.calculation.width,
                originalHeight: item.calculation.height
              }
            };
          });
        }
        // Convert to base (without VAT) - TPV prices include VAT, we store base like CalculatorPro
        const itemUnitPriceBase = (item.unitPrice || 0) / 1.21;
        // Send per-unit price — backend multiplies by quantity
        return {
          type: 'accesorio1',
          productId: item.productId || null,
          productName: item.name,
          quantity: item.quantity,
          unitPrice: itemUnitPriceBase,
          totalPrice: itemUnitPriceBase,  // Per unit — backend will multiply by quantity
          discountPercentage: item.itemDiscount || 0,
          metadata: {
            discount: item.itemDiscount,
            discountType: item.itemDiscountType,
            fingerprint: item.calculation?.fingerprint || `single-${item.id}`,
            calculationIndex: item.id || Date.now()
          }
        };
      }).flat();

      // Cuadrar componentes para que el detalle/Odoo/PDF reconstruyan el precio canónico (sin derivar céntimos)
      snapOrderItemsToCanonical(orderItems, ticketItems);

      // Calculate amounts - item.total includes VAT, calculate base for storage
      const totalWithVat = ticketItems.reduce((sum, item) => sum + (item.total || 0), 0);
      const discountAmountWithVat = globalDiscount === 0 ? 0 :
        (globalDiscountType === '%' ? totalWithVat * (globalDiscount / 100) : Math.min(globalDiscount, totalWithVat));
      const finalTotalWithVat = roundToFiveCents(totalWithVat - discountAmountWithVat);
      // Extract base (without VAT) from final total
      const baseSubtotal = finalTotalWithVat / 1.21;
      const baseDiscountAmount = discountAmountWithVat / 1.21;
      const baseAfterDiscount = baseSubtotal;
      const taxAmount = finalTotalWithVat - baseSubtotal;
      const finalTotal = finalTotalWithVat;

      const orderNotes = formData?.notes || calculation?.notes || `Pedido TPV #${activeSession.sessionNumber} (Pendiente)`;

      const orderData = {
        calculation: {
          client: { id: selectedClient.id, name: selectedClient.name },
          total: finalTotal,
          pricePerUnit: baseAfterDiscount / ticketItems.reduce((sum, item) => sum + item.quantity, 0),
          units: ticketItems.reduce((sum, item) => sum + item.quantity, 0),
          subtotal: baseSubtotal,
          tax: taxAmount,
          discount: globalDiscountType === '%' ? globalDiscount : 0,
          discountAmount: baseDiscountAmount,
          displayName: ticketItems.map(item => item.name).join(', '),
          fingerprint: ticketItems[0]?.calculation?.fingerprint || `tpv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          items: orderItems.length > 0 ? orderItems : [{
            type: 'accesorio1',
            productId: null,
            productName: 'Venta directa TPV',
            quantity: 1,
            unitPrice: baseAfterDiscount,
            totalPrice: baseAfterDiscount,
            metadata: {}
          }],
          notes: orderNotes,
          measurementType: ticketItems[0]?.calculation?.measurementType || 'INT',
          width: ticketItems[0]?.calculation?.width || 0,
          height: ticketItems[0]?.calculation?.height || 0
        },
        notes: orderNotes,
        clientReference: `TPV-${activeSession.sessionNumber}-${Date.now()}`,
        supplierReference: '',
        metadata: {
          isTPVSale: true,
          sessionId: activeSession.id,
          savedOnly: true, // Mark as saved only (not paid)
          timestamp: new Date().toISOString(),
          targetDeliveryDate: targetDeliveryDate || null
        },
        targetDeliveryDate: targetDeliveryDate || null
      };

      // Create the order (but don't process TPV transaction)
      const savedOrder = await api.post('/calculator/save-order', orderData);
      const orderId = savedOrder.data.order?.id;

      if (!orderId) {
        throw new Error('No se pudo crear el pedido');
      }

      // Invalidate orders cache
      queryClient.invalidateQueries(['orders']);

      toast.success(`✅ Pedido guardado: ${formatCurrency(total)}`);
      toast.info('📋 El pedido está pendiente de cobro');

      // Clear ticket
      setTicketItems([]);
      setCashReceived('');
      setShowCashInput(false);
      setGlobalDiscount(0);
      setTargetDeliveryDate('');

      if (onPaymentSuccess) {
        onPaymentSuccess({
          total,
          items: ticketItems,
          savedOnly: true,
          order: savedOrder.data.order
        });
      }

      // Show post-action modal in Venta Tienda mode
      if (staticMode && orderId) {
        openPostActionModal(orderId, 'save', total);
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(`Error al guardar: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Post-action modal handlers (Venta Tienda only)
  const openPostActionModal = (orderId, type, total) => {
    setPostActionOrderId(orderId);
    setPostActionType(type);
    setPostActionTotal(total);
    setShowPostActionModal(true);
  };

  const closePostActionModal = () => {
    setShowPostActionModal(false);
    setPostActionOrderId(null);
    setPostActionType(null);
    setPostActionTotal(0);
  };

  const handlePrintPresupuesto = async () => {
    if (!postActionOrderId || isPrintingPdf) return;
    setIsPrintingPdf(true);
    try {
      const pdf = await orderService.getPresupuestoTPVPDF(postActionOrderId);
      const url = URL.createObjectURL(pdf);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error printing presupuesto:', error);
      toast.error('Error al generar el presupuesto');
    } finally {
      setIsPrintingPdf(false);
    }
  };

  const handlePrintTicket = async () => {
    if (!postActionOrderId || isPrintingPdf) return;
    setIsPrintingPdf(true);
    try {
      const pdf = await orderService.getTicketTPVPDF(postActionOrderId);
      const url = URL.createObjectURL(pdf);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error printing ticket:', error);
      toast.error('Error al generar el ticket');
    } finally {
      setIsPrintingPdf(false);
    }
  };

  // Numpad handlers
  const handleNumpadClick = (value) => {
    if (value === 'C') {
      setNumpadValue('');
    } else if (value === '.') {
      if (!numpadValue.includes('.')) {
        setNumpadValue(numpadValue + '.');
      }
    } else if (value === '←') {
      setNumpadValue(numpadValue.slice(0, -1));
    } else if (value === 'OK') {
      if (editingField === 'quantity' && editingItem) {
        handleQuantityEdit(editingItem, numpadValue);
      } else if (editingField === 'unitPrice' && editingItem) {
        handleUnitPriceEdit(editingItem, numpadValue);
      } else if (editingField === 'cashReceived') {
        setCashReceived(numpadValue);
      } else if (editingField === 'globalDiscount') {
        setGlobalDiscount(parseFloat(numpadValue) || 0);
      }
      setShowNumpad(false);
      setNumpadValue('');
      setEditingItem(null);
      setEditingField(null);
    } else {
      setNumpadValue(numpadValue + value);
    }
  };

  // Format price - ALWAYS show without tax in item list
  const formatItemPrice = (price) => {
    // Always show base price without VAT in the item list
    return formatCurrency(price);
  };

  // Calculate item total - ALWAYS show without tax in item list
  const getDisplayTotal = (item) => {
    // Always show base total without VAT in the item list
    return item.total;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  // Inicializar configuraciones cuando se abre el TPV
  useEffect(() => {
    if (isOpen && !tpvSettings.isLoading) {
      // No establecer método de pago por defecto - el usuario debe elegir
      // setPaymentMethod(tpvSettings.defaultPaymentMethod);
    }
  }, [isOpen, tpvSettings.isLoading]);

  // Notify parent when ticket items change
  useEffect(() => {
    if (onTicketItemsChange) {
      onTicketItemsChange(ticketItems);
    }
  }, [ticketItems, onTicketItemsChange]);

  useEffect(() => {
    if (!isOpen || !externalAddToTicketSignal) return;
    handleAddToTicket();
  }, [externalAddToTicketSignal, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Check if there's a new calculation to add
    if (isOpen && calculation && calculation.fingerprint !== lastAddedCalculation?.fingerprint) {
      // DISABLED: Auto-add first calculation
      // Now users must manually click "Añadir Artículo" button to add items
      // This gives users more control over what goes into the ticket
      
      // Show a toast to notify user they can add the item
      if (calculation.total > 0) {
        showToast('info', '📦 Nueva configuración disponible - Pulsa "Añadir Artículo" para añadirla');
      }
    }
  }, [calculation, isOpen]);

  // DISABLED: Smart update logic
  // Each calculation is now treated as a separate potential item
  // Users can add multiple items with the same dimensions but different materials
  // This gives users full control over what goes into the ticket
  /*
  useEffect(() => {
    // This functionality has been disabled to allow adding multiple items
    // with the same dimensions but different materials
  }, [calculation]);
  */


  const subtotal = calculateSubtotal();
  const tax = calculateTax();
  const total = calculateTotal();
  
  // Debug current state
  console.log('TPV: Current state:', {
    ticketItems: ticketItems.length,
    subtotal,
    tax,
    total,
    calculation: calculation ? {
      total: calculation.total,
      subtotal: calculation.subtotal,
      pricePerUnit: calculation.pricePerUnit
    } : null
  });

  return (
    <>
      {!staticMode && (
        <FloatingTpvButton 
          $isOpen={isOpen}
          $hasItems={ticketItems.length > 0}
          $itemCount={ticketItems.length}
          onClick={() => !isOpen && onClose()}
          style={{ display: isOpen ? 'none' : 'flex' }}
        >
          🛒
        </FloatingTpvButton>
      )}
      
      <DrawerContainer $isOpen={isOpen} $staticMode={staticMode}>
        <DrawerHeader $staticMode={staticMode}>
          <DrawerTitle $staticMode={staticMode}>
            🏪 Terminal Punto de Venta
            {activeSession && (
              <span style={{ fontSize: '0.875rem', color: '#666', fontWeight: 'normal' }}>
                #{activeSession.sessionNumber}
              </span>
            )}
          </DrawerTitle>
          <HeaderActions>
            <HistoryButton 
              onClick={() => setShowHistory(true)}
              title="Historial de Tickets"
            >
              📊
            </HistoryButton>
            <SettingsButton 
              onClick={() => setShowSettings(true)}
              title="Configuración TPV"
            >
              ⚙️
            </SettingsButton>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </HeaderActions>
        </DrawerHeader>
        
        <MainContent>
          <TicketSection>
            <TicketHeader $staticMode={staticMode}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div>
                  <strong>📅 FECHA DE ENTREGA</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <span style={{ fontSize: '0.8rem', color: '#666' }}>📅</span>
                  <input
                    type="date"
                    value={targetDeliveryDate}
                    onChange={(e) => setTargetDeliveryDate(e.target.value)}
                    placeholder="Fecha entrega"
                    style={{
                      padding: '0.25rem 0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      color: '#333',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    title="Fecha de entrega objetivo (opcional)"
                  />
                </div>
              </div>
              {calculation && (
                <AddToTicketButton
                  onClick={handleAddToTicket}
                  $hasNew={calculation?.fingerprint !== lastAddedCalculation?.fingerprint}
                  disabled={calculation?.fingerprint === lastAddedCalculation?.fingerprint}
                >
                  <span>➕</span>
                  {calculation?.fingerprint === lastAddedCalculation?.fingerprint ? 'Ya añadido' : 'Añadir Artículo'}
                </AddToTicketButton>
              )}
            </TicketHeader>
            
            <TicketItems $staticMode={staticMode}>
              {ticketItems.length === 0 ? (
                <EmptyTicket>
                  <span style={{ fontSize: '3rem' }}>🛒</span>
                  <p><strong>Ticket vacío</strong></p>
                  <p>Calcula artículos en la calculadora y añádelos aquí</p>
                </EmptyTicket>
              ) : (
                ticketItems.map(item => (
                  <TicketItem key={item.id} $staticMode={staticMode}>
                    <ItemName>
                      {item.name}
                      {item.itemDiscount > 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#dc3545' }}>
                          -{item.itemDiscount}{item.itemDiscountType === '%' ? '%' : '€'} descuento
                        </div>
                      )}
                    </ItemName>
                    <ItemQuantity>
                      <QuantityButton onClick={() => updateQuantity(item.id, -1)}>−</QuantityButton>
                      <QuantityDisplay
                        value={item.quantity}
                        onChange={(e) => !isAdmin && handleQuantityEdit(item.id, e.target.value)}
                        onClick={() => {
                          if (!isAdmin) return;
                          setEditingItem(item.id);
                          setEditingField('quantity');
                          setNumpadValue(item.quantity.toString());
                          setShowNumpad(true);
                        }}
                        readOnly={isAdmin}
                        style={isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}}
                      />
                      <QuantityButton onClick={() => updateQuantity(item.id, 1)}>+</QuantityButton>
                    </ItemQuantity>
                    <ItemPrice>
                      <PriceRow>
                        <div
                          className="price-display"
                          onClick={() => {
                            if (!isAdmin) return;
                            setEditingItem(item.id);
                            setEditingField('unitPrice');
                            setNumpadValue(item.unitPrice.toString());
                            setShowNumpad(true);
                          }}
                          title={isAdmin ? "Click para editar precio" : ""}
                          style={isAdmin ? { cursor: 'pointer' } : {}}
                        >
                          {formatItemPrice(item.unitPrice)}/ud
                        </div>
                      </PriceRow>
                      <PriceRow>
                        <strong>{formatCurrency(getDisplayTotal(item))}</strong>
                      </PriceRow>
                      {tpvSettings.settings.enableQuickDiscounts && (
                        <DiscountControls>
                          {tpvSettings.getActiveQuickDiscounts().map((discount, index) => (
                            <DiscountButton 
                              key={index}
                              $active={item.itemDiscount === discount.value}
                              onClick={() => applyQuickDiscount(item.id, item.itemDiscount === discount.value ? 0 : discount.value)}
                            >
                              {discount.label}
                            </DiscountButton>
                          ))}
                        </DiscountControls>
                      )}
                    </ItemPrice>
                    <RemoveButton onClick={() => removeItem(item.id)}>×</RemoveButton>
                  </TicketItem>
                ))
              )}
            </TicketItems>
            
            <TicketFooter>
              <TotalRow>
                <span>Subtotal (sin IVA):</span>
                <span>{formatCurrency(subtotal)}</span>
              </TotalRow>
              
              {globalDiscount > 0 && (
                <TotalRow>
                  <span>Dto. Global ({globalDiscount}{globalDiscountType}):</span>
                  <span>-{formatCurrency(calculateGlobalDiscountAmount(subtotal))}</span>
                </TotalRow>
              )}
              
              {tpvSettings.showGlobalDiscountSection && (
                <GlobalDiscountSection>
                  <div className="discount-label">Descuento Global:</div>
                  <div className="discount-controls">
                    <input
                      className="discount-input"
                      type="number"
                      value={globalDiscount}
                      onChange={(e) => {
                        if (isAdmin) return; // Admin uses numpad
                        const value = parseFloat(e.target.value) || 0;
                        const maxAllowed = tpvSettings.getMaxAllowedDiscount(globalDiscountType === '%');
                        if (value <= maxAllowed) {
                          setGlobalDiscount(value);
                        } else {
                          toast.warning(`Descuento máximo permitido: ${maxAllowed}${globalDiscountType}`);
                        }
                      }}
                      onClick={() => {
                        if (!isAdmin) return;
                        setEditingField('globalDiscount');
                        setNumpadValue(globalDiscount.toString());
                        setShowNumpad(true);
                      }}
                      placeholder="0"
                      readOnly={isAdmin}
                      style={isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}}
                    />
                    <div className="discount-buttons">
                      <DiscountButton 
                        $active={globalDiscountType === '%'}
                        onClick={() => setGlobalDiscountType('%')}
                      >%</DiscountButton>
                      <DiscountButton 
                        $active={globalDiscountType === '€'}
                        onClick={() => setGlobalDiscountType('€')}
                      >€</DiscountButton>
                    </div>
                  </div>
                </GlobalDiscountSection>
              )}
              
              <TotalRow>
                <span>IVA (21%):</span>
                <span>{formatCurrency(tax)}</span>
              </TotalRow>
              
              <TotalRow $large>
                <span>TOTAL (IVA incl.):</span>
                <span>{formatCurrency(total)}</span>
              </TotalRow>
            </TicketFooter>
          </TicketSection>
          
          <ActionSection>
            <PaymentMethods>
              <PaymentButton
                $selected={paymentMethod === 'cash'}
                onClick={() => handlePaymentMethodSelect('cash')}
              >
                <span>💵</span>
                Efectivo
              </PaymentButton>
              <PaymentButton
                $selected={paymentMethod === 'card'}
                onClick={() => handlePaymentMethodSelect('card')}
              >
                <span>💳</span>
                Tarjeta
              </PaymentButton>
              
            </PaymentMethods>
            
            {/* Info message for non-cash payments */}
            {(paymentMethod === 'card' || paymentMethod === 'transfer') && (
              <div style={{
                padding: '0.75rem',
                background: '#e3f2fd',
                borderRadius: '4px',
                textAlign: 'center',
                color: '#1565c0',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                {paymentMethod === 'card' 
                  ? '💳 Procesando pago con tarjeta - No requiere introducir importe'
                  : '🏦 Registrando transferencia - No requiere introducir importe'}
              </div>
            )}
            
            <CashInputSection $show={showCashInput}>
              <CashInputRow>
                <label>{paymentMethod === 'mixed' ? 'Parte en efectivo:' : 'Recibido:'}</label>
                <input
                  type="number"
                  value={cashReceived}
                  onChange={(e) => !isAdmin && setCashReceived(e.target.value)}
                  onClick={() => {
                    if (!isAdmin) return;
                    setEditingField('cashReceived');
                    setNumpadValue(cashReceived);
                    setShowNumpad(true);
                  }}
                  placeholder="0.00"
                  step="0.01"
                  readOnly={isAdmin}
                  style={isAdmin ? { cursor: 'pointer', background: '#f8f9fa' } : {}}
                />
              </CashInputRow>
              
              <QuickAmountButtons>
                {tpvSettings.getActiveQuickCashAmounts().map((amount, index) => (
                  <QuickAmountButton 
                    key={index}
                    onClick={() => handleQuickAmount(amount)}
                  >
                    {amount}{tpvSettings.currencySymbol}
                  </QuickAmountButton>
                ))}
              </QuickAmountButtons>
              
              {cashReceived && (
                <ChangeDisplay $positive={calculateChange() >= 0}>
                  <span>Cambio:</span>
                  <span>{formatCurrency(calculateChange())}</span>
                </ChangeDisplay>
              )}
            </CashInputSection>
            
            <ActionButtonsRow>
              <ActionButton
                $variant="secondary"
                onClick={handleSaveOnly}
                disabled={ticketItems.length === 0 || isSaving || isProcessingPayment}
              >
                <span>{isSaving ? '⏳' : '💾'}</span>
                {isSaving ? 'GUARDANDO...' : 'GUARDAR'}
              </ActionButton>
              <ActionButton
                onClick={handleProcessPayment}
                disabled={ticketItems.length === 0 || isProcessingPayment || isSaving}
              >
                <span>{isProcessingPayment ? '⏳' : '💰'}</span>
                {isProcessingPayment ? 'PROCESANDO...' : 'COBRAR'}
              </ActionButton>
            </ActionButtonsRow>
            
            {!activeSession && (
              <div style={{ 
                padding: '0.75rem', 
                background: '#fff3cd', 
                borderRadius: '4px',
                textAlign: 'center',
                color: '#856404'
              }}>
                ⚠️ No hay sesión TPV activa
              </div>
            )}
          </ActionSection>
        </MainContent>
      </DrawerContainer>
      
      {/* Numpad Modal - Only for admin users */}
      {isAdmin && <NumpadModal $show={showNumpad}>
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '0.5rem',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          {editingField === 'quantity' && 'Editar Cantidad'}
          {editingField === 'unitPrice' && 'Editar Precio Unitario'}
          {editingField === 'cashReceived' && 'Importe Recibido'}
          {editingField === 'globalDiscount' && 'Descuento Global'}
        </div>
        <NumpadDisplay>
          {editingField === 'quantity' 
            ? numpadValue 
            : formatCurrency(parseFloat(numpadValue) || 0)
          }
        </NumpadDisplay>
        
        <NumpadGrid>
          <NumpadButton onClick={() => handleNumpadClick('7')}>7</NumpadButton>
          <NumpadButton onClick={() => handleNumpadClick('8')}>8</NumpadButton>
          <NumpadButton onClick={() => handleNumpadClick('9')}>9</NumpadButton>
          
          <NumpadButton onClick={() => handleNumpadClick('4')}>4</NumpadButton>
          <NumpadButton onClick={() => handleNumpadClick('5')}>5</NumpadButton>
          <NumpadButton onClick={() => handleNumpadClick('6')}>6</NumpadButton>
          
          <NumpadButton onClick={() => handleNumpadClick('1')}>1</NumpadButton>
          <NumpadButton onClick={() => handleNumpadClick('2')}>2</NumpadButton>
          <NumpadButton onClick={() => handleNumpadClick('3')}>3</NumpadButton>
          
          <NumpadButton onClick={() => handleNumpadClick('0')} $wide>0</NumpadButton>
          <NumpadButton onClick={() => handleNumpadClick('.')}>.</NumpadButton>
          
          <NumpadButton onClick={() => handleNumpadClick('←')} $special>←</NumpadButton>
          <NumpadButton onClick={() => handleNumpadClick('C')} $danger>C</NumpadButton>
          <NumpadButton onClick={() => handleNumpadClick('OK')} $success>OK</NumpadButton>
        </NumpadGrid>
        
        <button 
          onClick={() => setShowNumpad(false)}
          style={{
            width: '100%',
            padding: '0.75rem',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            marginTop: '0.5rem',
            cursor: 'pointer'
          }}
        >
          Cancelar
        </button>
      </NumpadModal>}

      {/* Settings Modal */}
      <TpvSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={tpvSettings.settings}
        onSave={tpvSettings.saveSettings}
      />

      {/* History Modal */}
      <TicketHistory
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        clientId={formData?.clientId}
      />

      {/* Post-action Modal (Venta Tienda only) */}
      <PostActionOverlay $show={showPostActionModal} onClick={closePostActionModal}>
        <PostActionModal onClick={e => e.stopPropagation()}>
          <PostActionTitle>
            {postActionType === 'charge' ? '✅ Venta completada' : '💾 Pedido guardado'}
          </PostActionTitle>
          <PostActionAmount $paid={postActionType === 'charge'}>
            {formatCurrency(postActionTotal)}
          </PostActionAmount>
          <PostActionButtons>
            <PostActionButton
              $variant="primary"
              onClick={handlePrintPresupuesto}
              disabled={isPrintingPdf}
            >
              📄 {isPrintingPdf ? 'Generando...' : 'Imprimir Presupuesto'}
            </PostActionButton>
            {postActionType === 'charge' && (
              <PostActionButton
                $variant="success"
                onClick={handlePrintTicket}
                disabled={isPrintingPdf}
              >
                🧾 {isPrintingPdf ? 'Generando...' : 'Imprimir Ticket'}
              </PostActionButton>
            )}
            <PostActionButton onClick={closePostActionModal}>
              Cerrar
            </PostActionButton>
          </PostActionButtons>
        </PostActionModal>
      </PostActionOverlay>
    </>
  );
};

export default TpvDrawer;
